// Sidebar: navigation (entries in menu.ts), level, device and network status.
import { level, onChange } from '../data/store.js';
import { devices } from '../devices/manager.js';
import { net } from '../net/client.js';
import { remote } from '../net/remote.js';
import { esc, toast } from './dom.js';
import { currentUser, onAuthChange } from '../data/api.js';
import { onSyncChange, syncStatus } from '../data/sync.js';
import { logo, icon } from './brand.js';
import { incomingCount, onFriendsChange } from '../data/friends.js';
import { t, onCultureChange } from '../i18n/index.js';
import { titleName } from '../i18n/content.js';
import { SECTIONS, SETTINGS, TUTORIALS, HUBS, type Hub, type HubId, type NavId } from './menu.js';


/** Section containing the current screen (navigation id given by the route). */
export const hubOf = (nav: string) => HUBS.find(h => h.tabs.some(([, n]) => n === nav) || h.also?.includes(nav));
const hubLabel = (h: Hub) => (h.tabs.length > 1 ? t.shell.hubs[h.id as HubId] : t.shell.nav[h.id as NavId]);
const tabLabel = (n: NavId) => (t.shell.tabs as Partial<Record<NavId, string>>)[n] ?? t.shell.nav[n];
const badge = '<b class="nav-count" hidden></b>';

export function mountShell() {
  const sb = document.getElementById('sidebar');
  let navId = '';
  const content = document.getElementById('content');
  const link = (h: Hub) => `<a href="#${h.tabs[0][0]}" data-nav="${h.id}" class="${hubOf(navId) === h ? 'on' : ''}">${icon(h.icon, 18)}${h.id === 'community' ? badge : ''}<span>${esc(hubLabel(h))}</span></a>`;
  /** Tabs of the current section, placed above the screen (the router empties the content on each route). */
  const drawTabs = () => {
    content.querySelector('.hub-tabs')?.remove();
    const h = hubOf(navId);
    if (!h || h.tabs.length < 2) return;
    const nav = document.createElement('nav');
    nav.className = 'hub-tabs';
    nav.innerHTML = h.tabs.map(([p, n], i) => `<a href="#${p}" class="${n === navId || (i === 0 && h.also?.includes(navId)) ? 'on' : ''}">${esc(tabLabel(n))}${n === 'friends' ? badge : ''}</a>`).join('');
    content.prepend(nav);
    paintCount();
  };
  const build = () => {
    const S = t.shell;
    sb.innerHTML = `
    <a class="brand" href="#/" aria-label="${esc(S.brandLabel)}">${logo(34)}</a>
    <nav class="nav" aria-label="${esc(S.menu)}">${SECTIONS.map(sec => `${sec.id ? `<div class="nav-label">${S.sections[sec.id]}</div>` : ''}${sec.hubs.map(link).join('')}`).join('')}</nav>
    <nav class="nav nav-end">${link(TUTORIALS)}${link(SETTINGS)}</nav>
    <div class="sb-foot">
      <div class="lvl" id="sb-lvl"></div>
      <div class="nav-label">${S.connections}</div>
      <div class="devs">
        <div class="chip" data-dev="account" title="${esc(S.chips.account)}">${icon('user', 15)}<span class="t"></span><span class="dot"></span></div>
        <div class="chip" data-dev="trainer" title="${esc(S.chips.trainer)}">${icon('bike', 15)}<span class="t"></span><span class="dot"></span></div>
        <div class="chip" data-dev="hr" title="${esc(S.chips.hr)}">${icon('heart', 15)}<span class="t"></span><span class="dot"></span></div>
        <div class="chip" data-dev="ride" title="${esc(S.chips.ride)}">${icon('gamepad', 15)}<span class="t"></span><span class="dot"></span></div>
        <div class="chip" data-dev="net" title="${esc(S.chips.net)}">${icon('wifi', 15)}<span class="t"></span><span class="dot"></span></div>
      </div>
    </div>`;
  };
  build();
  const paint = () => {
    const L = level(), S = t.shell, labels = S.labels;
    sb.querySelector('#sb-lvl').innerHTML = `<div class="top"><span class="lv-n">${L.level}</span><span class="lv-t"><b>${esc(titleName(L.level))}</b><small class="num">${L.xp - L.cur} / ${L.next - L.cur} XP</small></span></div><div class="bar"><i style="width:${(L.frac * 100).toFixed(1)}%"></i></div>`;
    // "trainer" chip: the best power source (trainer, power meter or speed sensor)
    // "ride" chip: the Zwift controllers, else a paired phone (away while it sleeps)
    const phone: ['on' | 'wait', string] | null = remote.phone === 'connected' ? ['on', S.phoneConnected] : remote.phone === 'away' ? ['wait', S.phoneConnected] : null;
    for (const k of ['trainer', 'hr', 'ride'] as const) {
      const [s, name] = k === 'trainer' ? devices.powerStatus : k === 'ride' && devices.status.ride[0] !== 'on' && phone ? phone : [devices.status[k][0], k === 'hr' ? devices.hr.name : S.rideConnected];
      const el = sb.querySelector(`[data-dev="${k}"]`);
      el.querySelector('.dot').className = 'dot ' + s;
      el.querySelector('.t').textContent = s === 'on' ? name : `${labels[k]}${s === 'wait' ? S.connecting : ''}`;
    }
    const acc = sb.querySelector('[data-dev="account"]'), u = currentUser();
    acc.querySelector('.dot').className = 'dot ' + (u ? (syncStatus.state === 'offline' || syncStatus.state === 'error' ? 'wait' : 'on') : '');
    acc.querySelector('.t').textContent = u ? `${u.name}${syncStatus.state === 'syncing' ? S.syncing : syncStatus.state === 'offline' ? S.offline : ''}` : S.signedOut;
    paintCount();
    const n = sb.querySelector('[data-dev="net"]');
    n.querySelector('.dot').className = 'dot ' + (net.status === 'on' ? 'on' : net.status === 'connecting' ? 'wait' : '');
    n.querySelector('.t').textContent = net.status === 'on' ? (net.room ? S.room(net.room.code) : S.serverOn) : labels.net;
  };
  function paintCount() {
    const pending = currentUser() ? incomingCount() : 0;
    for (const fc of document.querySelectorAll<HTMLElement>('.nav-count')) { fc.hidden = !pending; fc.textContent = String(pending); fc.title = pending ? t.shell.friendRequests(pending) : ''; }
  }
  sb.addEventListener('click', e => {
    const c = (e.target as HTMLElement).closest('.chip') as HTMLElement;
    if (c) location.hash = c.dataset.dev === 'net' ? (net.room ? '/online' : '/play/friends') : '/settings/devices';
    if ((e.target as HTMLElement).closest('#sb-lvl')) location.hash = '/progress';
  });
  devices.addEventListener('status', paint);
  devices.autoConnect();
  // a phone paired before a page reload comes back by itself; paired from elsewhere than the dialog, it is announced
  remote.addEventListener('change', paint);
  remote.addEventListener('paired', () => { if (!document.querySelector('[data-pair]')) toast(t.setup.pair.pairedToast, 'good'); });
  // the phone left by itself: no connection is kept waiting for another one unless the pairing dialog is open
  remote.addEventListener('unpaired', () => { if (remote.active && !document.querySelector('[data-pair]')) { remote.stop(); toast(t.setup.pair.unpairedToast, 'info'); } });
  remote.restore();
  net.addEventListener('status', paint); net.addEventListener('room', paint);
  onChange(paint);
  onAuthChange(paint); onSyncChange(paint); onFriendsChange(paint);
  document.addEventListener('route', (e: CustomEvent<{ nav: string }>) => {
    navId = e.detail.nav;
    const h = hubOf(navId);
    for (const a of sb.querySelectorAll<HTMLElement>('[data-nav]')) a.classList.toggle('on', a.dataset.nav === h?.id);
    drawTabs();
  });
  // Language change: rebuild the bar (the listeners, delegated on `sb`, stay in place).
  onCultureChange((_, langChanged) => { if (langChanged) build(); paint(); });
  paint();
}
